---
meta:
  - title: Plantillas de Word para exportar - IUSPRO Docs
  - description: Aprenda a aplicar el membrete y la presentación de su despacho a documentos creados con IA.
---

# Plantillas de Word para exportar

<div className="my-8 overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-[0_12px_35px_-28px_rgba(15,23,42,0.45)]">
	<div className="grid sm:grid-cols-[0.9fr_1.1fr]">
		<div className="bg-slate-900 px-6 py-7 text-slate-50">
			<span className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-300">La IA redacta</span>
			<div className="mt-2 font-instrument-serif text-2xl leading-tight">Su plantilla le da la presentación final.</div>
		</div>
		<div className="flex items-center px-6 py-7 text-sm leading-6 text-slate-600">
			Guarde una vez el membrete de su despacho y úselo al descargar distintos documentos creados en Auri o en el
			Redactor.
		</div>
	</div>
</div>

Una plantilla de Word para exportar es un archivo `.docx` reutilizable. IUSPRO coloca el contenido creado por la IA en
ese archivo y conserva elementos visuales como el encabezado, el pie de página, los logos, los márgenes y el tamaño de
página.

La plantilla cambia la **presentación del documento descargado**; no cambia el análisis ni las instrucciones que usa la
IA para redactarlo.

## Preparar el archivo en Word

Antes de subir la plantilla:

1. Cree un documento `.docx` de **una sola sección**
2. Coloque el membrete, los logos y otros elementos visuales en el **encabezado o pie de página**
3. Escriba `{{contenido}}` en el cuerpo, en una línea aparte, donde debe comenzar el texto creado por la IA
4. Acepte o rechace los cambios pendientes si el documento tiene el control de cambios activo
5. Guarde el archivo con un tamaño máximo de **100 MB**

> Use `{{contenido}}` una sola vez y déjelo solo en su propia línea. IUSPRO lo reemplazará con el documento generado.

## Guardar una plantilla personal

<a href="/settings/plantillas-word" target="_blank">
	Ir a mis plantillas de Word →
</a>

1. Haga clic en su **nombre o correo** en la esquina inferior izquierda
2. Entre a **Preferencias**
3. Abra **Plantillas de Word**
4. Arrastre el archivo `.docx` o haga clic para seleccionarlo
5. Escriba el nombre que verá después en el menú de descarga
6. Seleccione **Guardar plantilla**

La plantilla personal queda disponible únicamente en su cuenta.

## Guardar una plantilla para la organización

Las personas con rol de **propietario o administrador** pueden guardar plantillas para toda la organización:

1. Entre a **Administración**
2. Abra **Personalización**
3. Suba y nombre la plantilla
4. Seleccione **Guardar plantilla**

Todos los miembros activos de la organización podrán usarla al exportar. Solo los propietarios y administradores pueden
agregar, reemplazar, renombrar o eliminar estas plantillas.

Puede guardar hasta **20 plantillas personales** y hasta **20 plantillas de la organización**.

## Probar y administrar una plantilla

Después de guardarla, use **Probar** para descargar un documento de muestra. Ábralo en Word y revise:

- la posición y el tamaño de los logos
- el encabezado y el pie de página
- los márgenes y el tamaño de página
- el punto donde comienza el contenido

Desde el menú de acciones también puede descargar el archivo original, renombrar la plantilla, reemplazarla por una
versión nueva o eliminarla.

## Usar la plantilla al exportar

### Desde Auri

1. Abra el menú de descarga de un documento creado por Auri
2. Seleccione **Descargar Word**
3. Elija la plantilla que quiere aplicar

### Desde el Redactor

1. Abra el documento terminado
2. Seleccione **Exportar**
3. Abra **Descargar Word**
4. Elija la plantilla que quiere aplicar

El menú separa las plantillas de su organización de sus plantillas personales. También puede descargar un Word sin
plantilla.

## Plantilla de exportación o “Mis Formatos”

Use una **plantilla de Word para exportar** cuando quiera aplicar la misma presentación visual a documentos distintos.
Se aplica al descargar el documento.

Use **Mis Formatos** cuando quiera indicarle al Redactor cómo debe desarrollar, ordenar o estructurar cierto tipo de
documento. El formato participa desde la creación del borrador.

Puede combinar ambos: redactar con un Formato y aplicar el membrete de su organización al exportar.

## Si Word rechaza el archivo

Revise que:

- sea un archivo `.docx`, no `.doc` ni `.dotx`
- tenga una sola sección
- `{{contenido}}` aparezca una sola vez y en su propia línea
- los logos y cuadros de texto estén en el encabezado o pie, no en el cuerpo
- no tenga cambios pendientes del control de cambios

Si no ve **Plantillas de Word** en Preferencias ni en el menú de descarga, la función todavía no está habilitada para su
cuenta.
